import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { TodoSectionResponse } from '@/lib/api';

interface EditSectionDialogProps {
  open: boolean;
  onClose: () => void;
  section: TodoSectionResponse | null;
  sections: TodoSectionResponse[];
  onSave: (
    sectionId: string,
    input: { name?: string; description?: string; parentSectionId?: string | null },
  ) => void;
}

export function EditSectionDialog({
  open,
  onClose,
  section,
  sections,
  onSave,
}: EditSectionDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [parentSectionId, setParentSectionId] = useState<string>('none');

  useEffect(() => {
    if (open && section) {
      setName(section.name);
      setDescription(section.description ?? '');
      setParentSectionId(section.parentSectionId ?? 'none');
    }
  }, [open, section]);

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      onClose();
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!section || !name.trim()) return;

    const newParent = parentSectionId === 'none' ? null : parentSectionId;
    const oldParent = section.parentSectionId ?? null;

    onSave(section.sectionId, {
      name: name.trim(),
      description: description.trim() || undefined,
      ...(newParent !== oldParent ? { parentSectionId: newParent } : {}),
    });
    onClose();
  }

  const isTimeframe = section?.sectionType === 'timeframe';

  // Filter out the section itself and its descendants to prevent circular parents
  const availableParents = (() => {
    if (!section) return [];
    // Collect all descendant IDs
    const descendantIds = new Set<string>();
    const collectDescendants = (parentId: string) => {
      for (const s of sections) {
        if (s.parentSectionId === parentId && !descendantIds.has(s.sectionId)) {
          descendantIds.add(s.sectionId);
          collectDescendants(s.sectionId);
        }
      }
    };
    collectDescendants(section.sectionId);

    return sections.filter((s) => {
      if (s.sectionId === section.sectionId) return false;
      if (descendantIds.has(s.sectionId)) return false;
      return true;
    });
  })();

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit {isTimeframe ? 'Timeframe' : 'Section'}</DialogTitle>
          <DialogDescription>
            Update the name and description of this {isTimeframe ? 'timeframe' : 'section'}.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-section-name">Name</Label>
            <Input
              id="edit-section-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-section-description">Description (optional)</Label>
            <Textarea
              id="edit-section-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>

          {!isTimeframe && (
            <div className="space-y-2">
              <Label>Parent Section</Label>
              <Select value={parentSectionId} onValueChange={setParentSectionId}>
                <SelectTrigger>
                  <SelectValue placeholder="None (top-level)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (top-level)</SelectItem>
                  {availableParents.map((s) => (
                    <SelectItem key={s.sectionId} value={s.sectionId}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim()}>
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
